/*
 * The Private License
 * Copyright (c) 2023 Mahon Media LLC.
 */
import React from "react";
import { useLocation, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import UpsellScheduleManager from "../../components/UpsellScheduleManager";
import { getBackendBaseUrl } from "../../utils/envUtils";

const UpsellSchedulePage = () => {
  const { state } = useLocation();
  const { id } = useParams(); // undefined on /upsell/schedules/new

  // If we navigated from the list we’ll have the full schedule in state.
  const [initialSchedule, setInitialSchedule] = React.useState(state?.schedule ?? null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // If user refreshed on /:id/edit (no location.state), fetch that schedule.
  React.useEffect(() => {
    if (!id || initialSchedule) return; // create route or already have data

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const HOST = getBackendBaseUrl();
        const token = Cookies.get("jwtToken");
        const res = await fetch(`${HOST}/api/v1/upsell-schedule/${id}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error("Failed to fetch schedule");
        const s = await res.json();
        setInitialSchedule({
          id: s.id ?? s._id ?? s.scheduleId, // normalize id
          ...s,
        });
      } catch (e) {
        setError(e.message || "Failed to fetch schedule");
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, initialSchedule]);

  // Optional lightweight UI while fetching a single schedule on edit pages
  if (id && !initialSchedule && loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (id && !initialSchedule && error) return <div style={{ padding: 16, color: "crimson" }}>{error}</div>;

  return (
    <UpsellScheduleManager
      initialSchedule={initialSchedule}
      scheduleId={id || undefined} // lets the manager treat it as edit if id exists
    />
  );
};

export default UpsellSchedulePage;
